import React from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { FormField } from '../../../../service/types/types';

interface FormFieldsEditorProps {
  fields: FormField[];
  onChange: (fields: FormField[]) => void;
}

/**
 * Editor de preguntas de un formulario: agregar, quitar, editar y reordenar (↑/↓).
 * El orden final que ve el backend es el orden del array al guardar (ver forms.service.ts).
 * Se usa tanto para crear un formulario nuevo como para editar uno existente.
 */
export default function FormFieldsEditor({ fields, onChange }: FormFieldsEditorProps) {
  const addField = () => {
    onChange([...fields, { id: uuidv4(), label: '', type: 'text', required: false }]);
  };

  const updateField = (id: string, key: keyof FormField, value: any) => {
    onChange(fields.map((f) => (f.id === id ? { ...f, [key]: value } : f)));
  };

  const removeField = (id: string) => {
    onChange(fields.filter((f) => f.id !== id));
  };

  const moveField = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= fields.length) return;
    const reordenados = [...fields];
    [reordenados[index], reordenados[target]] = [reordenados[target], reordenados[index]];
    onChange(reordenados);
  };

  return (
    <>
      {fields.map((field, index) => (
        <Card key={field.id} className="mb-3 p-3">
          <Row className="align-items-start">
            <Col md={1} className="d-flex flex-column align-items-center gap-1">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={index === 0}
                onClick={() => moveField(index, -1)}
                title="Subir pregunta"
              >
                ↑
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={index === fields.length - 1}
                onClick={() => moveField(index, 1)}
                title="Bajar pregunta"
              >
                ↓
              </Button>
            </Col>
            <Col md={3}>
              <Form.Control
                type="text"
                placeholder="Etiqueta"
                value={field.label}
                onChange={(e) => updateField(field.id, 'label', e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Select value={field.type} onChange={(e) => updateField(field.id, 'type', e.target.value)}>
                <option value="text">Texto</option>
                <option value="number">Número</option>
                <option value="date">Fecha</option>
                <option value="select">Select</option>
                <option value="trabajadores">Trabajadores</option>
                <option value="predios">Predios</option>
                <option value="location">Ubicación</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              {field.type === 'select' && (
                <Form.Control
                  type="text"
                  placeholder="Opciones separadas por coma"
                  defaultValue={field.options?.join(',') ?? ''}
                  onChange={(e) => updateField(field.id, 'options', e.target.value.split(','))}
                />
              )}
            </Col>
            <Col md={2}>
              <Form.Check
                type="checkbox"
                label="Requerido"
                checked={field.required}
                onChange={(e) => updateField(field.id, 'required', e.target.checked)}
              />
            </Col>
          </Row>
          <Button variant="outline-danger" size="sm" className="mt-2" onClick={() => removeField(field.id)}>
            Eliminar
          </Button>
        </Card>
      ))}

      <Button variant="primary" onClick={addField} className="mb-3">
        + Agregar campo
      </Button>
    </>
  );
}
